/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-04 16:42:06
 **/

import { Dispatch, SetStateAction, useMemo } from "react";

import { Column, Grid, Row, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";

import { CallAvatar, ScreenShareVideo, UserVideoWithAvatar } from "@/frameworks/core/components";
import { usePhoneCall } from "@/frameworks/core/hooks";
import { removeMeFromList } from "@/frameworks/core/utils";
import EndScreenShare from "./EndScreenShare";

type MediaLayoutProps = {
	layout: PushServices.Call.UITypes;
} & Partial<{
	callID: string;
	isScreenShare: boolean;
	callAddUserList: string[];
	removeUser: Dispatch<SetStateAction<WebServices.User.UserInfo[]>>;
}>;

export default function GroupCallMediaLayout({
	layout,
	callID,
	// isScreenShare,
	callAddUserList,
	removeUser
}: MediaLayoutProps) {
	const phoneCall = usePhoneCall();
	const callUsers = phoneCall.state.users(callID ?? "")[0];
	const myInfo = useMemo(() => callUsers?.[Configure.userID], [callUsers]);
	const busyUser = phoneCall.state.busyUsers;
	const userList = phoneCall.state.participatedUserList(callID ?? "");
	const userListWithBusyUser = [...userList, ...busyUser];

	const screenSharedUserID = useMemo(
		() => callUsers && Object.values(callUsers)?.filter((userInfo) => userInfo?.media?.screenShare)[0]?.userId,
		[callUsers]
	);

	const getCallStatus = (callStatus: PushServices.Call.Status, index: number) => {
		if (callStatus === "REQUESTED") {
			return "calling";
		}
		if (["REJECTED", "HANGUP_NO_RESPONSE", "HANGUP"].includes(callStatus)) {
			return "no_response";
		}
		if (index === 0 || callStatus === "ACCEPTED") {
			return "none";
		}
		return "before_call";
	};

	const returnAvatarGroup = (
		callAddUserList: string[],
		busyUser: string[],
		callUsers: PushServices.Call.UserInfos,
		callID: string,
		userList: string[]
	) => {
		return (
			<Row gap={12} horizonAlign="center">
				{callAddUserList && callAddUserList?.length > 0 ? (
					[...new Set([...callAddUserList, ...busyUser])]?.map((userID, index) => {
						const callStatus = callUsers?.[userID]?.status;
						const callUserName = callUsers?.[userID]?.name;
						return (
							<Column
								key={userID}
								horizonAlign="center"
								onClick={() =>
									!callID && removeUser?.((prev) => [...prev.filter((u) => u.userID !== userID)])
								}
								gap={16}
								style={{ width: "88px" }}
							>
								<CallAvatar
									useStatus={false}
									callType={
										busyUser.includes(userID) ? "no_response" : getCallStatus(callStatus, index)
									}
									id={userID}
									key={userID}
									showMiniProfile={false}
									size="large"
								/>
								<TypographyTooltip.div
									tooltipProps={{
										hasIcon: false
									}}
									componentProps={{
										typoSize: "Body3_MD",
										ellipsis: true,
										maxLine: 1,
										stretch: true,
										align: "center"
									}}
								>
									{callUserName}
								</TypographyTooltip.div>
							</Column>
						);
					})
				) : (
					<Column key={Configure.userID} horizonAlign="center">
						<CallAvatar
							useStatus={false}
							id={Configure.userID}
							key={Configure.userID}
							showMiniProfile={false}
							size="large"
						/>
					</Column>
				)}
				{callID &&
					userList &&
					removeMeFromList(userList)?.map((userID) => {
						return (
							<UserVideoWithAvatar
								key={userID}
								callID={callID}
								style={{ display: "none" }}
								userID={userID}
								kind="people_80"
								isExpandIcon={true}
							/>
						);
					})}
			</Row>
		);
	};

	return (
		<>
			<SwitchCase
				condition={layout}
				cases={{
					AVATARONLY: returnAvatarGroup(callAddUserList || [], busyUser, callUsers, callID || "", userList),
					VIDEOCALL_WITH_TWO_USER: (
						<Column id="parent" style={{ width: "100%", paddingTop: "16px" }} gap={8}>
							<UserVideoWithAvatar
								callID={callID || ""}
								style={{ width: "380px", height: "213px" }}
								userID={Configure.userID}
								kind="people_80"
								isExpandIcon={true}
							/>
							<UserVideoWithAvatar
								callID={callID || ""}
								style={{ width: "380px", height: "213px" }}
								userID={callAddUserList?.[0] || ""}
								kind="people_80"
								isExpandIcon={true}
							/>
						</Column>
					),
					GRID_UI: (
						<Maybe
							test={
								callUsers &&
								Object.values(callUsers)?.filter((call) => call?.media?.video === "on").length > 0
							}
							fallback={returnAvatarGroup(
								callAddUserList || [],
								busyUser,
								callUsers,
								callID || "",
								userList
							)}
						>
							<Grid gridGap={10} column={2}>
								{userListWithBusyUser &&
									userListWithBusyUser.map((userID, index) => {
										const callStatus = callUsers?.[userID]?.status;
										return (
											<UserVideoWithAvatar
												callType={getCallStatus(callStatus, index)}
												key={`vwa-${index}`}
												callID={callID || ""}
												style={{ width: "176px", height: "99px" }}
												userID={userID || ""}
												kind="people_80"
												isExpandIcon={true}
											/>
										);
									})}
							</Grid>
						</Maybe>
					)
				}}
				defaultCase={
					<Grid gridGap={10} column={2}>
						{userListWithBusyUser &&
							userListWithBusyUser.map((userID, index) => {
								const callStatus = callUsers?.[userID]?.status;
								return (
									<UserVideoWithAvatar
										callType={getCallStatus(callStatus, index)}
										key={`vwa-${index}`}
										callID={callID || ""}
										style={{ width: "176px", height: "99px" }}
										userID={userID || ""}
										kind="people_80"
										isExpandIcon={true}
									/>
								);
							})}
					</Grid>
				}
			/>
			<Maybe test={Boolean(screenSharedUserID)}>
				<Maybe test={myInfo?.media?.screenShare}>
					<EndScreenShare callID={callID} />
					<Maybe.Fallback>
						<Row style={{ width: "100%" }}>
							<ScreenShareVideo
								style={{ width: "392px", height: "220px" }}
								userID={
									(myInfo &&
										(myInfo?.media?.screenShare
											? myInfo?.userId
											: screenSharedUserID
												? screenSharedUserID
												: "")) ||
									""
								}
								callID={callID ?? ""}
								isExpandIcon={true}
							/>
						</Row>
					</Maybe.Fallback>
				</Maybe>
			</Maybe>
		</>
	);
}
